import React, { useEffect, useState } from "react";
import { adminApi } from "../lib/api";
import { toast } from "sonner";
import { Users, AlertTriangle, RefreshCw, Merge } from "lucide-react";

/**
 * DuplicateLeadsCard
 *
 * Admin-only Settings card that scans leads for duplicate phones and lets
 * an admin merge them into a single canonical row. We keep this UI tight
 * (preview → merge) because phone duplicates are typically caused by
 * webhook race-conditions or pre-dedup historical data.
 */
const DuplicateLeadsCard = () => {
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [data, setData] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.listDuplicateLeads();
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load duplicates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleMerge = async () => {
    if (!window.confirm(
      `Merge ${data?.total_duplicates || 0} duplicate lead row(s) into their canonical sibling? ` +
      `Messages, orders, follow-ups, payment links, notes and call logs from the duplicates will be re-pointed to the canonical lead. The duplicate rows will then be deleted. This is one-way.`
    )) return;
    setMerging(true);
    setLastResult(null);
    try {
      const r = await adminApi.mergeDuplicateLeads({});
      setLastResult(r.data);
      toast.success(`Merged ${r.data?.total_merged || 0} duplicate row(s) across ${r.data?.groups_processed || 0} phone(s)`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMerging(false);
    }
  };

  const totalGroups = data?.total_groups || 0;
  const totalDups = data?.total_duplicates || 0;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="duplicate-leads-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-purple-600" />
          <h3 className="text-sm font-bold">Duplicate Leads</h3>
        </div>
        <button
          onClick={refresh}
          disabled={loading}
          className="text-[11px] text-gray-600 hover:text-gray-900 flex items-center gap-1"
          data-testid="dup-leads-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="text-[12px] text-gray-500 py-6 text-center">Scanning leads…</div>
      ) : totalDups === 0 ? (
        <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="dup-leads-clean">
          No duplicate phone numbers found. Each customer appears in chats exactly once.
        </div>
      ) : (
        <>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex items-start gap-2" data-testid="dup-leads-summary">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
            <div className="text-[12px] text-amber-900 flex-1">
              Found <b>{totalDups}</b> duplicate row(s) across <b>{totalGroups}</b> phone number(s). Merging keeps the lead with the most messages and re-points all related data (messages, orders, calls, notes) onto it.
            </div>
          </div>

          <div className="max-h-56 overflow-auto border border-gray-100 rounded-lg mb-3" data-testid="dup-leads-list">
            <table className="w-full text-[11px]">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Phone</th>
                  <th className="text-left px-2 py-1.5">Rows</th>
                  <th className="text-left px-2 py-1.5">Names</th>
                  <th className="text-left px-2 py-1.5">WATI</th>
                </tr>
              </thead>
              <tbody>
                {(data?.groups || []).slice(0, 100).map((g) => (
                  <tr key={g.phone} className="border-t border-gray-100">
                    <td className="px-2 py-1.5 font-mono">{g.phone}</td>
                    <td className="px-2 py-1.5 font-bold text-amber-700">{g.count}</td>
                    <td className="px-2 py-1.5 truncate max-w-[140px]" title={(g.names || []).join(" / ")}>{(g.names || []).join(" / ") || "—"}</td>
                    <td className="px-2 py-1.5">{Array.from(new Set(g.wati_accounts || [])).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={handleMerge}
            disabled={merging}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs font-bold rounded-lg px-3 py-2 flex items-center gap-1.5"
            data-testid="dup-leads-merge-btn"
          >
            <Merge className="w-3.5 h-3.5" />
            {merging ? "Merging…" : `Merge ${totalDups} duplicate${totalDups === 1 ? "" : "s"}`}
          </button>
        </>
      )}

      {lastResult && (
        <div className="mt-3 text-[11px] bg-gray-50 border border-gray-200 rounded-lg p-2" data-testid="dup-leads-result">
          Last merge: <b>{lastResult.total_merged}</b> row(s) merged across <b>{lastResult.groups_processed}</b> group(s).
        </div>
      )}
    </div>
  );
};

export default DuplicateLeadsCard;
